import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL, apiUrl } from "../config/api";

const ContentTabs = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("gallery");
  const [galleryData, setGalleryData] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loadedImages, setLoadedImages] = useState(new Set());
  const [hoveredId, setHoveredId] = useState(null);

  const hasGallery = galleryData.length > 0;
  const hasMessages = messages.length > 0;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [galleryRes, msgRes] = await Promise.all([
          fetch(apiUrl("/api/content/gallery")),
          fetch(apiUrl("/api/content/daily-messages")),
        ]);

        if (!galleryRes.ok || !msgRes.ok) {
          throw new Error("API Error");
        }

        setGalleryData(await galleryRes.json());
        setMessages(await msgRes.json());
      } catch (fetchError) {
        console.error("Error fetching data:", fetchError);
        setError(fetchError.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (hasGallery && hasMessages) return;
    if (hasGallery && activeTab !== "gallery") setActiveTab("gallery");
    if (hasMessages && activeTab !== "messages") setActiveTab("messages");
  }, [activeTab, hasGallery, hasMessages]);

  const handleImageLoad = (id) => {
    setLoadedImages((prev) => new Set([...prev, id]));
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + "M";
    if (num >= 1000) return (num / 1000).toFixed(1) + "K";
    return num?.toString() || "0";
  };

  if (error) {
    return (
      <div className="explore-error">
        <div className="error-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </div>
        <h3>خطا در بارگذاری</h3>
        <p>{error}</p>
        <button onClick={() => window.location.reload()} className="retry-btn">
          تلاش مجدد
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="explore-loading-container">
        <div className="explore-loading-spinner" />
      </div>
    );
  }

  return (
    <div className="content-container">
      <div className="explore-header">
        <div className="explore-tabs">
          {hasGallery && (
            <button
              className={`explore-tab ${activeTab === "gallery" ? "active" : ""}`}
              onClick={() => setActiveTab("gallery")}
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069z" />
                <path d="M12 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8z" />
              </svg>
              <span>پست‌ها</span>
            </button>
          )}

          {hasMessages && (
            <button
              className={`explore-tab ${activeTab === "messages" ? "active" : ""}`}
              onClick={() => setActiveTab("messages")}
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z" />
              </svg>
              <span>پیام‌ها</span>
            </button>
          )}
        </div>
      </div>

      {activeTab === "gallery" && (
        <div className="explore-container">
          <div className="explore-grid">
            {galleryData.map((item, index) => (
              <button
                key={item.id}
                type="button"
                className={`explore-item ${loadedImages.has(item.id) ? "loaded" : ""}`}
                style={{
                  animationDelay: `${(index % 9) * 50}ms`,
                  aspectRatio: item.aspectRatio || getRandomAspectRatio(item.id),
                }}
                onMouseEnter={() => setHoveredId(item.id)}
                onMouseLeave={() => setHoveredId(null)}
                onClick={() => navigate(`/gallery/${item.id}`)}
              >
                {!loadedImages.has(item.id) && (
                  <div className="explore-skeleton">
                    <div className="skeleton-shimmer" />
                  </div>
                )}

                <img
                  src={`${API_BASE_URL}${item.imageUrl}`}
                  alt={item.title || ""}
                  loading="lazy"
                  onLoad={() => handleImageLoad(item.id)}
                />

                <div className={`explore-overlay ${hoveredId === item.id ? "visible" : ""}`}>
                  <div className="overlay-stats">
                    <div className="stat-item">
                      <svg viewBox="0 0 24 24" fill="white">
                        <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                      </svg>
                      <span>{formatNumber(item.likes)}</span>
                    </div>
                    <div className="stat-item">
                      <svg viewBox="0 0 24 24" fill="white">
                        <path d="M21.99 4c0-1.1-.89-2-1.99-2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h14l4 4-.01-18z" />
                      </svg>
                      <span>{formatNumber(item.comments?.length || 0)}</span>
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {activeTab === "messages" && (
        <div className="messages-container">
          <div className="messages-grid">
            {messages.map((msg, index) => (
              <div
                key={msg.id}
                className="message-card"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="message-header">
                  <div className="message-icon">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z" />
                    </svg>
                  </div>
                  <span className="message-date">{msg.date}</span>
                </div>
                <h3 className="message-title">{msg.title}</h3>
                <p className="message-content">{msg.content}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const getRandomAspectRatio = (id) => {
  const ratios = ["1/1", "4/5", "1/1", "4/5", "1/1", "4/5"];
  return ratios[id % ratios.length];
};

export default ContentTabs;
